// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Landbridge — bootstrap and the screen machine. Owns which screen is up, the
// room's Net (created ONCE per session, never left for a rematch), and the glue
// between the pure game/Race and the DOM. All rules live in game.ts; the map in
// render.ts; the input in search.ts.

import '@ben-gy/game-engine/mobile.css';
import './styles/main.css';

import { hardenViewport } from '@ben-gy/game-engine/mobile';
import { createSfx } from '@ben-gy/game-engine/sound';
import { createStore } from '@ben-gy/game-engine/storage';
import { newSeed } from '@ben-gy/game-engine/rng';
import { createNet, roomAppId, setTurnConfig, type Net } from '@ben-gy/game-engine/net';
import { getTurnConfig } from '@ben-gy/game-engine/turn';
import { createRounds, type RoundInfo, type Rounds, type RoundPlayer } from '@ben-gy/game-engine/rematch';
import {
  clearRoomInUrl,
  createLobby,
  createRoomEntry,
  normalizeRoomCode,
  setRoomInUrl,
} from '@ben-gy/game-engine/lobby';
import { resolveName } from '@ben-gy/game-engine/identity';

import { armConfirm, escapeHtml } from './dom';
import { MODE_IDS, modeOf, guessableIds, type Mode } from './modes';
import {
  activeHint,
  createGame,
  giveUp,
  hintNext,
  play,
  takeHint,
  undo,
  hops,
  overPar,
  destCountry,
  startCountry,
  type GameState,
} from './game';
import { ALL, country, resolveTyped, shortestPath } from './data';
import { dailySeed, todayUTC } from './seed';
import { createMapView, type MapView } from './render';
import { createSearch, type Search } from './search';
import { Race, seatOf, createOneShot, type RaceMsg } from './race';
import { runCountdown, type Countdown } from './countdown';
import { aboutHtml, ATTRIB, helpHtml, menuHtml, multiResultsHtml, soloResultsHtml } from './ui';

const SLUG = 'landbridge';

// ── shell ───────────────────────────────────────────────────────────────────

const app = document.querySelector<HTMLElement>('#app')!;
let content: HTMLElement;

const store = createStore(SLUG);
const sfx = createSfx(store.get<boolean>('muted', false) ?? false);
const reducedMotion =
  typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

let unlocked = false;
function unlock(): void {
  if (unlocked) return;
  unlocked = true;
  try {
    sfx.unlock();
  } catch {
    /* audio unlock is best-effort */
  }
}
window.addEventListener('pointerdown', unlock, { once: true });
window.addEventListener('keydown', unlock, { once: true });

// ── persistent session state ─────────────────────────────────────────────────

let playerName = resolveName(store, () => `Traveller ${Math.floor(Math.random() * 900 + 100)}`);
let myModeChoice = modeOf(store.get<string>('mode', '')).id;
let hostOptsMode = myModeChoice;
let streak = 0;

function bestFor(modeId: string): number {
  return store.get<number>(`best_${modeId}`, 0) ?? 0;
}
function setBest(modeId: string, v: number): void {
  if (v > bestFor(modeId)) store.set(`best_${modeId}`, v);
}

let net: Net | null = null;
let rounds: Rounds | null = null;
let race: Race | null = null;
let roster: RoundPlayer[] = [];
let tally: number[] = [];
let roomCode = '';

// ── screen lifecycle ─────────────────────────────────────────────────────────

const cleanups: Array<() => void> = [];
function onCleanup(fn: () => void): void {
  cleanups.push(fn);
}
function teardown(): void {
  for (const fn of cleanups.splice(0)) {
    try {
      fn();
    } catch {
      /* a failing teardown must never block the next screen */
    }
  }
}
function setPlaying(on: boolean): void {
  document.body.classList.toggle('playing', on);
}

function mountShell(): void {
  app.innerHTML = `
    <main class="main-content" id="content"></main>
    <footer class="site-footer">${ATTRIB}</footer>`;
  content = document.getElementById('content')!;
}

function sheet(html: string): void {
  const wrap = document.createElement('div');
  wrap.className = 'overlay';
  wrap.innerHTML = `<div class="sheet">${html}</div>`;
  document.body.appendChild(wrap);
  const close = (): void => wrap.remove();
  wrap.addEventListener('pointerdown', (e) => {
    if (e.target === wrap) close();
  });
  wrap.querySelectorAll('.sheet-close').forEach((b) => b.addEventListener('click', close));
}

// ── menu ─────────────────────────────────────────────────────────────────────

function showMenu(): void {
  teardown();
  setPlaying(false);
  content.innerHTML = menuHtml({
    modeId: myModeChoice,
    best: bestFor(myModeChoice),
    muted: sfx.muted(),
    dateStr: todayUTC(),
  });

  content.querySelectorAll<HTMLElement>('.chip').forEach((c) =>
    c.addEventListener('click', () => {
      myModeChoice = c.dataset.mode!;
      store.set('mode', myModeChoice);
      streak = 0;
      showMenu();
    }),
  );
  content.querySelector('#playDaily')!.addEventListener('click', () => startSolo(true));
  content.querySelector('#playRandom')!.addEventListener('click', () => startSolo(false));
  content.querySelector('#playFriends')!.addEventListener('click', showRoomEntry);
  content.querySelector('#help')!.addEventListener('click', () => sheet(helpHtml()));
  content.querySelector('#about')!.addEventListener('click', () => sheet(aboutHtml()));
  content.querySelector('#mute')!.addEventListener('click', () => {
    sfx.setMuted(!sfx.muted());
    store.set('muted', sfx.muted());
    showMenu();
  });

  if (!store.get<boolean>('seenHelp', false)) {
    store.set('seenHelp', true);
    sheet(helpHtml());
  }
}

// ── game screen (shared by solo + multiplayer) ───────────────────────────────

interface GameHooks {
  mode: Mode;
  getState: () => GameState;
  getMsLeft: () => number | null;
  onGuess: (id: string) => void;
  onUndo: () => void;
  onHint: () => void;
  onGiveUp: () => void;
  onQuit: () => void;
}

interface GameScreen {
  update(): void;
  destroy(): void;
}

function heartsHtml(state: GameState, mode: Mode): string {
  let s = '';
  for (let i = 0; i < mode.lives; i++) s += `<span class="heart${i < state.lives ? '' : ' spent'}">♥</span>`;
  return s;
}

function buildGameScreen(hooks: GameHooks): GameScreen {
  const mode = hooks.mode;
  content.innerHTML = `
    <section class="screen game">
      <div class="hud">
        <span class="hud-mode">${escapeHtml(mode.name)}</span>
        <span class="hud-goal" id="hudGoal"></span>
        <span class="hud-lives" id="hudLives"></span>
        <span class="hud-timer" id="hudTimer" hidden></span>
        <button class="linkbtn hud-quit" id="quit">Quit</button>
      </div>
      <div class="mapwrap">
        <canvas class="map" id="map"></canvas>
      </div>
      <div class="play">
        <div class="chainrow">
          <button class="linkbtn giveup" id="giveup">Give up</button>
          <div class="chainbar" id="chainbar" aria-live="polite"></div>
        </div>
        <p class="hint-line" id="hintLine" aria-live="polite" hidden></p>
        <div class="controls">
          <div class="search-host" id="searchHost"></div>
          <button class="btn hint" id="hint" aria-label="Name the best next country">Hint</button>
          <button class="btn undo" id="undo" aria-label="Undo last hop">Undo</button>
        </div>
      </div>
    </section>`;

  const canvas = content.querySelector<HTMLCanvasElement>('#map')!;
  const mapView: MapView = createMapView(canvas);
  (canvas as unknown as { __mv?: MapView }).__mv = mapView;
  const chainbar = content.querySelector<HTMLElement>('#chainbar')!;
  const hudGoal = content.querySelector<HTMLElement>('#hudGoal')!;
  const hudLives = content.querySelector<HTMLElement>('#hudLives')!;
  const hudTimer = content.querySelector<HTMLElement>('#hudTimer')!;
  const undoBtn = content.querySelector<HTMLButtonElement>('#undo')!;
  const hintBtn = content.querySelector<HTMLButtonElement>('#hint')!;
  const giveUpBtn = content.querySelector<HTMLButtonElement>('#giveup')!;
  const hintLine = content.querySelector<HTMLElement>('#hintLine')!;

  const poolSet = guessableIds();
  const search: Search = createSearch({
    container: content.querySelector<HTMLElement>('#searchHost')!,
    pool: () => poolSet,
    taken: () => new Set(hooks.getState().chain),
    onGuess: (id) => hooks.onGuess(id),
    placeholder: 'Name the next country…',
  });
  requestAnimationFrame(() => search.focus());

  content.querySelector('#quit')!.addEventListener('click', hooks.onQuit);
  undoBtn.addEventListener('click', hooks.onUndo);
  // A hint cannot be undone either — you cannot un-read the answer, and it spends
  // the round's only one — and it sits 8px from Undo in the same column, so it
  // takes the same two taps as Give up.
  const hintConfirm = armConfirm(hintBtn, {
    idle: 'Hint',
    armed: 'Sure?',
    onConfirm: hooks.onHint,
  });
  // Giving up cannot be undone — it ends the round and spoils the answer on the
  // map — so it takes two taps: the first only swaps the label to 'Sure?'. Any
  // other move cancels it again (see update()).
  const giveUpConfirm = armConfirm(giveUpBtn, {
    idle: 'Give up',
    armed: 'Sure?',
    onConfirm: hooks.onGiveUp,
  });

  function update(): void {
    const st = hooks.getState();
    const dest = destCountry(st);
    hudGoal.innerHTML =
      mode.objective === 'route'
        ? `<span class="goal-arw">to</span> <strong>${escapeHtml(dest?.name ?? '')}</strong> <span class="goal-par">par ${st.par}</span> · ${hops(st)} hop${hops(st) === 1 ? '' : 's'}`
        : `Chain · ${st.chain.length} countr${st.chain.length === 1 ? 'y' : 'ies'}`;
    hudLives.innerHTML = heartsHtml(st, mode);

    // Chain strip (start → … → tail).
    chainbar.innerHTML = st.chain
      .map((id, i) => {
        const role = id === st.startId ? 'start' : id === st.destId ? 'dest' : i === st.chain.length - 1 ? 'tail' : 'link';
        return `<span class="cpill ${role}">${escapeHtml(country(id)?.name ?? id)}</span>`;
      })
      .join('<span class="cpill-arw">›</span>');
    chainbar.scrollLeft = chainbar.scrollWidth;

    const ms = hooks.getMsLeft();
    if (ms !== null) {
      hudTimer.hidden = false;
      const s = Math.max(0, Math.ceil(ms / 1000));
      hudTimer.textContent = `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
      hudTimer.classList.toggle('low', s <= 15);
    }
    undoBtn.hidden = mode.objective !== 'route';
    undoBtn.disabled = st.over || st.chain.length <= 1;

    // The hint names the best next country; it never plays it, and it is spent by
    // the hop it named — activeHint() goes null once that hop is made.
    const spent = st.hintId !== null;
    hintBtn.disabled = st.over || spent || hintNext(st) === null;
    // Keyed on the state like Give up's, and for the same reason (see below).
    hintConfirm.disarmOn(st);
    // 'Used' rather than 'Hint used' — the label must not change width and shove
    // the search box mid-round; the hint line below carries the detail. While the
    // confirm is armed the label is its own ('Sure?') and a redraw must leave it
    // alone, or the 250ms net loop would paint 'Hint' back over it.
    if (!hintConfirm.armed()) hintBtn.textContent = spent ? 'Used' : 'Hint';
    const shown = activeHint(st);
    if (shown) {
      hintLine.hidden = false;
      hintLine.innerHTML = `Hint: <strong>${escapeHtml(country(shown)?.name ?? shown)}</strong> borders your last country`;
    } else {
      hintLine.hidden = true;
      hintLine.textContent = '';
    }
    giveUpBtn.disabled = st.over;
    // Keyed on the state, NOT on the redraw: a net round redraws every 250ms
    // (startNetRound's loop) and a bare disarm() there un-armed the button before
    // the player's second tap, making Give up impossible to complete online.
    // Every real move replaces the state object; a tick that changes nothing does not.
    giveUpConfirm.disarmOn(st);

    search.setDisabled(st.over);
    search.refresh();

    mapView.render({
      startId: st.startId,
      destId: st.destId,
      chain: st.chain,
      reveal: st.over,
      // The reveal the map has always been able to draw: the route you missed.
      optimal: st.over && st.destId ? shortestPath(st.startId, st.destId) : [],
      reducedMotion,
    });
  }

  update();

  return {
    update,
    // Expose the map so a misfire can shake it.
    // (attached below via a cast to keep the interface minimal)
    destroy() {
      search.destroy();
      mapView.destroy();
    },
  } as GameScreen & { _map?: MapView };
}

/** Sound + haptic feedback for the outcome of a play. */
function playFeedback(before: GameState, after: GameState): void {
  try {
    if (after.solved) sfx.play('win');
    else if (after.chain.length > before.chain.length) {
      sfx.play('coin', { pitch: 0.75 + Math.min(1, after.chain.length / 12) * 0.9 });
    } else if (after.lives < before.lives) sfx.play('hurt');
  } catch {
    /* audio best-effort */
  }
  if (!reducedMotion && 'vibrate' in navigator) {
    try {
      if (after.lives < before.lives) navigator.vibrate([20, 30, 20]);
      else if (after.chain.length > before.chain.length) navigator.vibrate(after.solved ? [40, 30, 60] : 12);
    } catch {
      /* haptics best-effort */
    }
  }
}

// ── solo ─────────────────────────────────────────────────────────────────────

let soloState: GameState | null = null;
let soloDaily = false;
let soloScreen: (GameScreen & { _map?: MapView }) | null = null;
let soloMap: MapView | null = null;

function startSolo(daily: boolean): void {
  teardown();
  setPlaying(true);
  const mode = modeOf(myModeChoice);
  soloDaily = daily;
  const seed = daily ? dailySeed(mode, todayUTC()) : newSeed();
  soloState = createGame(mode, seed);

  soloScreen = buildGameScreen({
    mode,
    getState: () => soloState!,
    getMsLeft: () => null,
    onGuess: (id) => {
      const before = soloState!;
      const next = play(before, id);
      if (next === before) {
        // A misfire that didn't change lives (invalid/noop) — nothing to do.
        return;
      }
      soloState = next;
      playFeedback(before, next);
      if (next.lives < before.lives) soloMap?.flashMisfire();
      soloScreen!.update();
      if (soloState.over) endSolo(daily);
    },
    onUndo: () => {
      const next = undo(soloState!);
      if (next === soloState) return;
      soloState = next;
      soloScreen!.update();
    },
    onHint: () => {
      const next = takeHint(soloState!);
      if (next === soloState) return;
      soloState = next;
      soloScreen!.update();
    },
    onGiveUp: () => {
      const next = giveUp(soloState!);
      if (next === soloState) return;
      soloState = next;
      // Straight through endSolo, so a conceded round scores exactly like any
      // other unsolved one: no streak, no new best.
      soloScreen!.update();
      endSolo(daily);
    },
    onQuit: () => {
      streak = 0;
      showMenu();
    },
  });
  soloMap = grabMap();
  onCleanup(() => soloScreen?.destroy());

  runCountdown(document.body, sfx, () => {});
}

/** Reach into the just-built game screen for its map (for misfire shake). */
function grabMap(): MapView | null {
  const cv = content.querySelector<HTMLCanvasElement>('#map');
  return cv ? (cv as unknown as { __mv?: MapView }).__mv ?? null : null;
}

function endSolo(daily: boolean): void {
  const st = soloState!;
  const mode = modeOf(st.modeId);
  if (mode.objective === 'route') {
    if (!daily) {
      if (st.solved) {
        streak++;
        setBest(mode.id, streak);
      } else {
        streak = 0;
      }
    }
  } else if (!daily) {
    setBest(mode.id, st.chain.length);
  }
  setTimeout(() => showSoloResults(), 850);
}

function showSoloResults(): void {
  teardown();
  setPlaying(false);
  const st = soloState!;
  const mode = modeOf(st.modeId);
  const best = bestFor(mode.id);
  content.innerHTML = `
    ${soloResultsHtml(st, mode, best)}
    <div class="menu-actions">
      <button class="btn primary" id="again">${soloDaily ? 'Play random' : 'Next route'}</button>
      <button class="btn" id="share">Share result</button>
      <button class="btn ghost" id="menu">Main menu</button>
      <button class="btn ghost" id="feedback">Report a problem</button>
    </div>`;
  content.querySelector('#again')!.addEventListener('click', () => startSolo(false));
  content.querySelector('#menu')!.addEventListener('click', showMenu);
  content.querySelector('#feedback')!.addEventListener('click', (e) =>
    window.feedback?.open({ returnFocusTo: e.currentTarget as HTMLElement }),
  );
  content.querySelector('#share')!.addEventListener('click', () => void shareSolo(st, mode));
}

function shareSolo(st: GameState, mode: Mode): Promise<void> {
  const head = soloDaily ? `Landbridge ${todayUTC()} · ${mode.name}` : `Landbridge · ${mode.name}`;
  let line: string;
  if (mode.objective === 'chain') line = `${st.chain.length}-country chain`;
  else if (st.solved) line = overPar(st) === 0 ? `perfect route in ${hops(st)}` : `${hops(st)} hops (par ${st.par})`;
  else if (st.reason === 'gaveup') line = `gave up (par ${st.par})`;
  else line = `didn't finish (par ${st.par})`;
  // The share text is a public claim; it must say the same thing the screen does.
  if (st.hintId) line += ' (with a hint)';
  const text = `${head} — ${line}\nhttps://landbridge.benrichardson.dev`;
  return share(text);
}

async function share(text: string): Promise<void> {
  try {
    if (navigator.share) {
      await navigator.share({ text });
      return;
    }
  } catch {
    /* cancelled — fall through to copy */
  }
  try {
    await navigator.clipboard.writeText(text);
    toast('Result copied');
  } catch {
    toast('Could not copy');
  }
}

function toast(msg: string): void {
  const t = document.createElement('div');
  t.className = 'toast';
  t.textContent = msg;
  document.body.appendChild(t);
  setTimeout(() => t.classList.add('show'), 10);
  setTimeout(() => t.remove(), 1900);
}

// ── multiplayer ──────────────────────────────────────────────────────────────

let netSend: (m: RaceMsg) => void = () => {};
let stOff: () => void = () => {};
let lobby: { repaint(): void; destroy(): void } | null = null;
let lobbyMounted = false;
let gameScreen: (GameScreen & { _map?: MapView }) | null = null;
let netMap: MapView | null = null;
let loopTimer: ReturnType<typeof setInterval> | null = null;

function repaintLobby(): void {
  if (lobbyMounted) lobby?.repaint();
}

function showRoomEntry(): void {
  teardown();
  setPlaying(false);
  content.innerHTML = '<section class="screen"><div id="entry"></div></section>';
  const host = content.querySelector<HTMLElement>('#entry')!;

  const deep = new URL(location.href).searchParams.get('room');
  if (deep) {
    const code = normalizeRoomCode(deep);
    clearRoomInUrl();
    if (code.length >= 3) {
      void enterRoom(code, false);
      return;
    }
  }

  const entry = createRoomEntry({
    container: host,
    onSubmit: (code, created) => void enterRoom(normalizeRoomCode(code), created),
    onCancel: showMenu,
    title: 'Play with friends',
    subtitle: 'Everyone races the same route. Start a room, or type a friend’s code.',
  });
  onCleanup(() => entry.destroy());
}

async function enterRoom(code: string, created: boolean): Promise<void> {
  teardown();
  setPlaying(false);
  roomCode = code;
  setRoomInUrl(code);
  tally = [];
  race = null;

  content.innerHTML = `<section class="screen"><div class="lobby-searching"><span class="spinner"></span> Connecting…</div></section>`;

  try {
    setTurnConfig(await getTurnConfig());
  } catch {
    /* fail open — TURN is an upgrade, never a dependency */
  }

  const n = createNet(
    { appId: roomAppId(SLUG), roomId: code, claimHost: created },
    {
      onHostChange: (_id, isSelfHost) => {
        race?.setHost(isSelfHost);
        repaintLobby();
      },
      onPeers: () => repaintLobby(),
      onPeerLeave: (id) => {
        const seat = seatOf(roster, id);
        if (seat !== null) race?.peerLeft(seat);
        repaintLobby();
        refreshMultiResults();
      },
      onPeerJoin: (id) => {
        const seat = seatOf(roster, id);
        if (seat !== null) race?.peerReturned(seat);
      },
    },
  );
  net = n;

  const r = createRounds<{ mode: string }>({
    net: n,
    playerName,
    minPlayers: 2,
    roundOpts: () => ({ mode: myModeChoice }),
    onRound: (info) => startNetRound(info),
    onChange: (s) => {
      const ho = s.hostOpts as { mode?: string } | null;
      if (ho && typeof ho.mode === 'string') hostOptsMode = modeOf(ho.mode).id;
      repaintLobby();
    },
  });
  rounds = r;

  const sendStatus = n.channel<RaceMsg>('st', (data, from) => {
    if (!race) return;
    const seat = seatOf(roster, from);
    if (seat === null) return;
    race.receive(data, seat);
    refreshMultiResults();
  });
  netSend = (m) => sendStatus(m);
  stOff = () => sendStatus.off();

  showLobby();
}

function showLobby(): void {
  const n = net;
  const r = rounds;
  if (!n || !r) return;
  lobbyMounted = true;
  setPlaying(false);

  content.innerHTML = `<section class="screen"><div id="lobby-host"></div></section>`;
  const isHost = (): boolean => n.isHost() && n.hostSettled();

  lobby = createLobby({
    container: content.querySelector<HTMLElement>('#lobby-host')!,
    net: n,
    rounds: r,
    roomCode,
    minPlayers: 2,
    maxPlayers: 6,
    onCancel: () => void leaveRoom(),
    modeSlot: () => {
      if (isHost()) {
        return `<div class="lobby-modes" id="lm">${MODE_IDS.map(
          (id) =>
            `<button class="chip mode-btn${id === myModeChoice ? ' on' : ''}" data-mode="${id}" aria-pressed="${id === myModeChoice}">${modeOf(id).name}</button>`,
        ).join('')}<p class="mode-blurb">${escapeHtml(modeOf(myModeChoice).blurb)}</p></div>`;
      }
      return `<p class="host-pick">${n.hostSettled() ? `The host chose <strong>${modeOf(hostOptsMode).name}</strong>` : 'Waiting for the host…'}</p>`;
    },
    onModeMount: () => {
      if (!isHost()) return;
      content.querySelectorAll<HTMLElement>('#lm .mode-btn').forEach((b) =>
        b.addEventListener('click', () => {
          myModeChoice = b.dataset.mode!;
          store.set('mode', myModeChoice);
          lobby?.repaint();
        }),
      );
    },
  });
  onCleanup(() => {
    lobby?.destroy();
    lobby = null;
  });
}

function startNetRound(info: RoundInfo<{ mode: string }>): void {
  teardown();
  lobbyMounted = false;
  roster = info.players;
  if (tally.length !== roster.length) tally = Array(roster.length).fill(0);
  const mode = modeOf(info.opts?.mode);
  const selfSeat = info.seated ? seatOf(roster, net?.selfId ?? '') : null;
  const names = roster.map((p) => p.name);

  const rc = new Race({
    mode,
    seed: info.seed,
    selfSeat,
    names,
    isHost: info.isHost,
    deps: {
      send: (m) => netSend(m),
      onChange: () => refreshMultiResults(),
      onSelfEnd: () => {
        rounds?.finish();
        setTimeout(() => showMultiResults(), 700);
      },
    },
  });
  race = rc;
  setPlaying(true);

  if (selfSeat === null) {
    showMultiResults();
  } else {
    gameScreen = buildGameScreen({
      mode,
      getState: () => rc.state,
      getMsLeft: () => rc.msLeft,
      onGuess: (id) => {
        const before = rc.state;
        if (rc.guess(id)) {
          playFeedback(before, rc.state);
          if (rc.state.lives < before.lives) netMap?.flashMisfire();
          gameScreen?.update();
        }
      },
      onUndo: () => {
        if (rc.undoHop()) gameScreen?.update();
      },
      onHint: () => {
        if (rc.hint()) gameScreen?.update();
      },
      onGiveUp: () => {
        rc.concede();
        gameScreen?.update();
      },
      onQuit: () => backToLobby(),
    });
    netMap = grabMap();
    onCleanup(() => {
      gameScreen?.destroy();
      gameScreen = null;
    });
    const cd: Countdown = runCountdown(document.body, sfx, () => {});
    onCleanup(() => cd.cancel());
  }

  loopTimer = setInterval(() => {
    rc.tick(250);
    gameScreen?.update();
  }, 250);
  onCleanup(() => {
    if (loopTimer) clearInterval(loopTimer);
    loopTimer = null;
  });
}

// ── multiplayer results ──────────────────────────────────────────────────────

let resultsMounted = false;
let winsAwarded = false;
const closeWait = createOneShot(20_000, () => refreshMultiResults());

function showMultiResults(): void {
  teardown();
  setPlaying(false);
  resultsMounted = true;
  winsAwarded = false;
  content.innerHTML = `
    <section class="screen results" id="mres"></section>
    <div class="menu-actions" id="mact">
      <button class="btn primary" id="again">Play again</button>
      <p class="waiting-note" id="waiting" aria-live="polite"></p>
      <button class="btn" id="lobby">Back to lobby</button>
      <button class="btn ghost" id="menu">Main menu</button>
      <button class="btn ghost" id="feedback">Report a problem</button>
    </div>`;
  const again = content.querySelector<HTMLButtonElement>('#again')!;
  again.addEventListener('click', () => {
    rounds?.vote();
    again.disabled = true;
    again.textContent = 'Waiting for the others…';
  });
  content.querySelector('#lobby')!.addEventListener('click', () => backToLobby());
  content.querySelector('#menu')!.addEventListener('click', () => void leaveRoom());
  content.querySelector('#feedback')!.addEventListener('click', (e) =>
    window.feedback?.open({ returnFocusTo: e.currentTarget as HTMLElement }),
  );
  onCleanup(() => {
    resultsMounted = false;
    closeWait.cancel();
  });
  closeWait.poke();
  refreshMultiResults();
}

function refreshMultiResults(): void {
  if (!resultsMounted || !race) return;
  const rc = race;
  const box = content.querySelector<HTMLElement>('#mres');
  if (!box) return;

  if (!winsAwarded && rc.everyoneDone()) {
    winsAwarded = true;
    const winner = rc.ranked().find((r) => !r.left && (rc.mode.objective === 'chain' ? r.chainLen > 1 : r.solved));
    if (winner) tally[winner.seat] = (tally[winner.seat] ?? 0) + 1;
  }

  box.innerHTML = multiResultsHtml(rc.ranked(), rc.selfSeat, rc.mode, rc.state.startId, rc.state.destId, tally);

  const waiting = content.querySelector<HTMLElement>('#waiting');
  if (waiting) {
    const r = rounds?.state();
    if (r && r.startsInMs !== null) {
      waiting.textContent = `Next round in ${Math.ceil(r.startsInMs / 1000)}s — ${r.votes.length}/${Math.max(r.present.length, 1)} ready`;
    } else if (!rc.everyoneDone()) {
      const left = [...rc.results.values()].filter((x) => !x.over).map((x) => x.name);
      waiting.textContent = left.length ? `Still routing: ${left.join(', ')}` : '';
    } else if (r && r.votes.length) {
      waiting.textContent = `${r.votes.length}/${Math.max(r.present.length, 1)} ready for another round`;
    } else {
      waiting.textContent = '';
    }
  }
}

function backToLobby(): void {
  teardown();
  race = null;
  if (net && rounds) showLobby();
  else showMenu();
}

async function leaveRoom(): Promise<void> {
  teardown();
  lobbyMounted = false;
  race = null;
  const n = net;
  const r = rounds;
  net = null;
  rounds = null;
  netSend = () => {};
  try {
    stOff();
  } catch {
    /* channel may already be gone */
  }
  stOff = () => {};
  r?.destroy();
  if (n) {
    try {
      await n.leave();
    } catch {
      /* leaving is best-effort */
    }
  }
  showMenu();
}

// ── boot ─────────────────────────────────────────────────────────────────────

function boot(): void {
  hardenViewport();
  mountShell();
  const room = new URL(location.href).searchParams.get('room');
  if (room && normalizeRoomCode(room).length >= 3) showRoomEntry();
  else showMenu();
  window.addEventListener('beforeunload', () => {
    void net?.leave();
  });
}

// A hook the browser-verification pass drives to play without the search UI
// (synthetic pointer events do not reliably reach a canvas). Test-only.
(window as unknown as { __lb?: unknown }).__lb = {
  playSolo: (nameOrId: string): GameState | null => {
    if (!soloState || !soloScreen) return null;
    const c = resolveTyped(nameOrId, guessableIds()) ?? country(nameOrId);
    if (!c) return null;
    const before = soloState;
    const next = play(before, c.id);
    if (next !== before) {
      soloState = next;
      playFeedback(before, next);
      if (next.lives < before.lives) soloMap?.flashMisfire();
      soloScreen.update();
      if (soloState.over) endSolo(soloDaily);
    }
    return soloState;
  },
  undoSolo: (): GameState | null => {
    if (!soloState || !soloScreen) return null;
    const next = undo(soloState);
    if (next !== soloState) {
      soloState = next;
      soloScreen.update();
    }
    return soloState;
  },
  state: (): GameState | null => soloState,
  startName: (): string | null => (soloState ? startCountry(soloState).name : null),
  destName: (): string | null => (soloState ? (destCountry(soloState)?.name ?? null) : null),
  allIds: (): string[] => ALL.map((c) => c.id),
};

boot();
